import { Event as TEvent } from "../../../../Global/Base/Events";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { Checkbox } from "../../../UI.BaseControls/Checkbox";
import { ChartType } from "../../../UI.Controls/ChartElement";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { ChartSettingsHeaderBuilder } from "../DataBuilders/ChartSettingsHeaderBuilder";
/**
 * Třída představuje sekci zobrazení v ChartSettingsPanel => ChartSettingsContentBuilder
 */
export class ChartDisplay {
    /** událost změny typu grafu */
    public chartTypeChanged: TEvent<ChartType, void> = new TEvent<ChartType, void>();
    /** událost změny orientace grafu */
    public chartOrientationChanged: TEvent<boolean, void> = new TEvent<boolean, void>();
    /** událost zobrazení / skrytí legendy */
    public showLegendChanged: TEvent<boolean, void> = new TEvent<boolean, void>();
    private readonly _element: HTMLElement;
    private readonly _chartType: ChartType;
    /** zobrazovat legendu */
    private _showLegend: boolean;
    /** zobrazovat graf horizontálně */
    private _isHorizontal: boolean;
    /** místo pro vkládání checkboxů  */
    private _displayContainer: HTMLElement;
    /** pole používaných checkboxů  */
    private _activeCheckboxes: Array<Checkbox> = [];
    /**
     * instance části ChartSettingsPanelu s tlačítky typu grafu
     * slouží pro
     * - vyvolání eventu změny typu grafu checkboxem
     * - získání sekce typu grafu
     * - získání aktuálně zvoleného tlačítka typ grafu
     */
    private _headerBuilder: ChartSettingsHeaderBuilder;
    /** hlavní css název třídy */
    private _cssClass: string = "chart-display";
    public get element(): HTMLElement {
        return this._element;
    }

    constructor(chartType: ChartType, showLegend: boolean, headerBuilder: ChartSettingsHeaderBuilder) {
        this._chartType = chartType;
        this._showLegend = showLegend;
        this._headerBuilder = headerBuilder;
        this._element = createElement({ classes: [this._cssClass] });
        this._initialize();
    }

    private _initialize(): void {
        const displayBoxText = createElement({ classes: [this._cssClass + "__header"] });
        Localization.addLocTxtData(displayBoxText, "GUI-623211"); // zobrazení

        this._displayContainer = createElement({ classes: [this._cssClass + "__block"] });
        this._fillChartDisplay(this._chartType);

        this.element.appendChild(displayBoxText);
        this.element.appendChild(this._displayContainer);
        this._headerBuilder.chartTypeChanged.add((chartType: ChartType) => this._fillChartDisplay(chartType));
    }

    /**
     * vybírá zobrazení dle zvoleného tlačítka typ grafu
     * @param chartType Typ grafu
     */
    private _fillChartDisplay(chartType: ChartType): void {
        switch (this._headerBuilder.getChartTypeSection(chartType)) {
            case ChartType.bar: {
                this._barDisplay();
                break;
            }
            case ChartType.pie: {
                this._pieDisplay();
                break;
            }
            case ChartType.line: {
                this._lineDisplay();
                break;
            }
        }
    }

    /**
     * zobrazí checkboxy pro typ grafu bar
     */
    private _barDisplay(): void {
        this.dispose();
        this._displayContainer.appendChild(this._getNewRow("GUI-230023", CheckboxAction.legend, this._showLegend)); //legenda
        this._displayContainer.appendChild(this._getNewRow("GUI-676681", CheckboxAction.horizontal, this._isHorizontal)); //Překlopit o 90°
    }

    /**
     * zobrazí checkboxy pro typ grafu pie
     */
    private _pieDisplay(): void {
        this.dispose();
        this._displayContainer.appendChild(this._getNewRow("GUI-230023", CheckboxAction.legend, this._showLegend)); //legenda
        this._displayContainer.appendChild(this._getNewRow("GUI-676684", CheckboxAction.doNotFillCenter, this._chartType === ChartType.doughnut)); //Nevyplňovat střed
    }

    /**
     * zobrazí checkboxy pro typ grafu line
     */
    private _lineDisplay(): void {
        this.dispose();
        this._displayContainer.appendChild(this._getNewRow("GUI-230023", CheckboxAction.legend, this._showLegend)); //legenda
        this._displayContainer.appendChild(this._getNewRow("GUI-676681", CheckboxAction.horizontal, this._isHorizontal)); //Překlopit o 90°
    }

    /**
     * vytvoří checkbox s danými parametry, vrátí jej a vloží jej do sekce Zobrazení
     * @param boxLabel Label checkboxu
     * @param action Slouží k výběru akce po kliknutí na checkbox
     * @param isChecked Ne/zvolit checkbox při vytvoření
     */
    private _getNewRow(boxLabel: string, action: string, isChecked: boolean = false): HTMLElement {
        const chartDisplayItemRow = createElement({ classes: [this._cssClass + "__row"] });
        const chartDisplayItemCell = createElement({ classes: [this._cssClass + "__cell"] });
        const chartDisplayItem = createElement({ classes: [this._cssClass + "__item"] });
        const a = new Checkbox();
        a.classList.add(this._cssClass + "__checkbox");
        a.checked = isChecked;
        a.standardAction = true;
        a.isNullable = false;
        a.setAttribute("data-action", action);
        Localization.addLocData(a, boxLabel, "text", "title");
        this._activeCheckboxes.push(a);
        chartDisplayItem.appendChild(a);
        a.addEventListener("change", () => {
            this._checkboxChanged(a);
        });
        chartDisplayItem.addEventListener("click", (e) => {
            this._checkboxClicked(e, a);
        });
        chartDisplayItemCell.appendChild(chartDisplayItem);
        chartDisplayItemRow.appendChild(chartDisplayItemCell);
        return chartDisplayItemRow;
    }

    /**
     * vrací zpět změnu checkboxu
     * nejdříve se spustí událost na změnu checkboxu
     * potom událost na klik na řádek s checkboxem
     * protože má checkbox reagovat i na řádek s checkboxem,
     * je změna checkboxu prováděna v eventu na klik na řádek s checkboxem
     * @param checkbox Checkbox
     */
    private _checkboxChanged(checkbox: Checkbox): void {
        checkbox.controlValue = !checkbox.controlValue;
    }

    /**
     * metoda spuštěná po kliku na checkbox
     * uloží změnu do ChartSettingsManageru a vyvolá event změny
     * @param e Event
     * @param checkbox Checkbox
     */
    private _checkboxClicked(e: Event, checkbox: Checkbox): void {
        e.preventDefault();
        if (checkbox.enabled) {
            checkbox.controlValue = !checkbox.controlValue;
        }

        const action = checkbox.getAttribute("data-action");
        const chbValue = !!checkbox.value;
        switch (action) {
            case CheckboxAction.legend:
                this._showLegend = chbValue;
                this.showLegendChanged.trigger(chbValue);
                break;
            case CheckboxAction.horizontal:
                this._isHorizontal = chbValue;
                this.chartOrientationChanged.trigger(chbValue);
                break;
            case CheckboxAction.doNotFillCenter:
                this.chartTypeChanged.trigger(chbValue ? ChartType.doughnut : this._headerBuilder.getActiveChartType());
                break;
        }
    }

    public dispose(): void {
        this._activeCheckboxes.forEach((checkbox) => {
            checkbox.dispose();
        });
        this._activeCheckboxes.splice(0, this._activeCheckboxes.length);
        while (this._displayContainer.childNodes.length > 0) {
            this._displayContainer.childNodes[0].remove();
        }
    }
}

export enum CheckboxAction {
    legend = "legend",
    horizontal = "horizontal",
    doNotFillCenter = "doNotFillCenter",
}
